import React from "react";
import { useMesStore } from "@/stores/useMesStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface CheckoutDialogProps {
  isManual?: boolean;
}

export default function CheckoutDialog({
  isManual = false,
}: CheckoutDialogProps) {
  const {
    isManualAddOpen,
    setManualAddOpen,
    activeSessionLogId,
    setActiveSessionLogId,
  } = useMesStore();
  const [qtyOk, setQtyOk] = React.useState<number | "">("");
  const [qtyNg, setQtyNg] = React.useState<number | "">("");
  const [notes, setNotes] = React.useState("");
  const queryClient = useQueryClient();

  const checkoutMutation = useMutation(trpc.mes.checkoutSession.mutationOptions({
    onSuccess: () => {
      toast.success("Báo cáo sản lượng thành công!");
      setManualAddOpen(false);
      setActiveSessionLogId(null);
      queryClient.invalidateQueries({ queryKey: [["mes", "listLogs"]] });
      queryClient.invalidateQueries({ queryKey: [["mes", "activeSessions"]] });
      queryClient.invalidateQueries({ queryKey: [["mes", "todaysPlan"]] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi báo cáo");
    },
  }));

  const handleSubmit = () => {
    if (!isManual && activeSessionLogId) {
      checkoutMutation.mutate({
        logId: activeSessionLogId,
        qtyOk: Number(qtyOk) || 0,
        qtyNg: Number(qtyNg) || 0,
        notes,
      });
    } else {
      // Placeholder for manual quick report
      toast.warning("Chức năng báo cáo thủ công (không phiên) đang phát triển");
    }
  };

  return (
    <Dialog open={isManualAddOpen} onOpenChange={setManualAddOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isManual ? "Báo cáo sản lượng (Thủ công)" : "Báo cáo sản lượng"}
          </DialogTitle>
          <DialogDescription>
            Nhập số lượng thành phẩm đạt và lỗi.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 py-4">
          {isManual && (
            <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
              <Label htmlFor="routingStepId">Mã Công đoạn / Lệnh</Label>
              <Input id="routingStepId" placeholder="Quét hoặc nhập mã..." />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
              <Label htmlFor="qtyOk" className="text-green-600 font-semibold">
                Số lượng Đạt (OK)
              </Label>
              <Input
                id="qtyOk"
                type="number"
                min="0"
                placeholder="0"
                value={qtyOk}
                onChange={(e) =>
                  setQtyOk(e.target.value === "" ? "" : Number(e.target.value))
                }
              />
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
              <Label htmlFor="qtyNg" className="text-red-600 font-semibold">
                Số lượng Lỗi (NG)
              </Label>
              <Input
                id="qtyNg"
                type="number"
                min="0"
                placeholder="0"
                value={qtyNg}
                onChange={(e) =>
                  setQtyNg(e.target.value === "" ? "" : Number(e.target.value))
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
            <Label htmlFor="notes">Ghi chú / Nguyên nhân lỗi</Label>
            <Textarea
              id="notes"
              placeholder="Nhập ghi chú hoặc lý do hàng NG..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="outline"
            onClick={() => {
              setManualAddOpen(false);
              setActiveSessionLogId(null);
            }}
          >
            Hủy
          </Button>
          <Button
            type="button"
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={handleSubmit}
            disabled={checkoutMutation.isPending}
          >
            Lưu Báo Cáo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
